import { Modal, ModalBody } from '@fluentsms/agentnet-web-components';
import { Box, Checkbox, createStyles, makeStyles, Theme, Typography } from '@material-ui/core';
import {
  CheckBox as CheckBoxIcon,
  CheckBoxOutlineBlank as CheckBoxOutlineBlankIcon,
  CheckCircleOutline,
} from '@material-ui/icons';
import clsx from 'clsx';
import { FC, ReactNode, useEffect, useMemo, useState } from 'react';
import AgentNetButton from 'ui-kit/components/button/AgentNetButton';
import { IconsMap } from 'ui-kit/icons/MarketplaceIcons';
import { ModalHeader } from '../ModalHeader';
import {
  FraudPreventionService,
  getAdditionalFraudPreventionServices,
  getFraudPreventionServiceByProductCode,
  getFraudPreventionServiceDisplayName,
} from './fraudPreventionServices';

const TEST_ID_PREFIX = 'enable-additional-services';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    section: {
      marginBottom: theme.spacing(3),
      '&:last-child': {
        marginBottom: 0,
      },
    },
    sectionTitle: {
      fontWeight: 600,
      marginBottom: theme.spacing(1.5),
    },
    sectionDescription: {
      marginBottom: theme.spacing(2),
      color: theme.palette.text.secondary,
    },
    serviceRow: {
      display: 'flex',
      alignItems: 'flex-start',
      gap: theme.spacing(2),
      padding: theme.spacing(2),
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: theme.shape.borderRadius,
      backgroundColor: theme.palette.common.white,
    },
    serviceRowSelected: {
      borderColor: theme.palette.primary.main,
      backgroundColor: theme.palette.brand.sky50,
    },
    serviceRows: {
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(1.5),
    },
    iconContainer: {
      alignItems: 'center',
      display: 'flex',
      flexShrink: 0,
      height: 48,
      justifyContent: 'center',
      maxHeight: 48,
      maxWidth: 48,
      width: 48,
      '& svg': {
        height: '100%',
        maxHeight: 48,
        maxWidth: 48,
        width: '100%',
      },
    },
    serviceContent: {
      flex: 1,
      minWidth: 0,
    },
    serviceTitle: {
      fontWeight: 600,
      marginBottom: 0,
    },
    servicePrice: {
      color: theme.palette.primary.main,
      marginBottom: theme.spacing(0.5),
      fontWeight: 500,
    },
    serviceAction: {
      display: 'flex',
      alignItems: 'center',
      flexShrink: 0,
      paddingTop: theme.spacing(0.5),
    },
    enabledIcon: {
      color: theme.palette.success.main,
      fontSize: 24,
    },
    checkbox: {
      padding: 0,
      '&:hover': {
        background: 'transparent',
      },
    },
    modalFooter: {
      alignItems: 'center',
      backgroundColor: theme.palette.brand.feather25,
      borderTop: `1px solid ${theme.palette.divider}`,
      display: 'flex',
      justifyContent: 'space-between',
      padding: theme.spacing(2, 3),
    },
  }),
);

interface ServiceRowContentProps {
  service: FraudPreventionService;
  action: ReactNode;
  testId: string;
  classes: ReturnType<typeof useStyles>;
  selected?: boolean;
}

const ServiceRowContent: FC<ServiceRowContentProps> = ({ service, action, testId, classes, selected = false }) => {
  const IconComponent = IconsMap[service.iconKey] ?? IconsMap.default;

  return (
    <Box className={clsx(classes.serviceRow, selected && classes.serviceRowSelected)} data-testid={testId}>
      <Box className={classes.iconContainer}>
        <IconComponent />
      </Box>
      <Box className={classes.serviceContent}>
        <Typography variant="h4" className={classes.serviceTitle}>
          {service.displayName}
        </Typography>
        {service.price ? (
          <Typography variant="body2" className={classes.servicePrice} data-testid={`${testId}-price`}>
            {service.price}
          </Typography>
        ) : null}
        <Typography variant="body2" color="textSecondary">
          {service.description}
        </Typography>
      </Box>
      <Box className={classes.serviceAction}>{action}</Box>
    </Box>
  );
};

export interface EnableAdditionalServicesDialogProps {
  open: boolean;
  currentProductCode: string;
  currentProductName: string;
  currentProductDescription: string;
  enabledProductCodes?: string[];
  isSubmitting?: boolean;
  onClose: () => void | Promise<void>;
  onCancel: () => void | Promise<void>;
  onContinue: (selectedProductCodes: string[]) => void | Promise<void>;
}

export const EnableAdditionalServicesDialog: FC<EnableAdditionalServicesDialogProps> = ({
  open,
  currentProductCode,
  currentProductName,
  currentProductDescription,
  enabledProductCodes = [],
  isSubmitting = false,
  onClose,
  onCancel,
  onContinue,
}) => {
  const classes = useStyles();
  const [selectedProductCodes, setSelectedProductCodes] = useState<string[]>([]);

  const enablingService = useMemo(() => {
    const service = getFraudPreventionServiceByProductCode(currentProductCode);

    if (service) {
      return service;
    }

    return {
      productCode: currentProductCode,
      productName: currentProductName,
      displayName: getFraudPreventionServiceDisplayName(currentProductName),
      iconKey: 'default',
      description: currentProductDescription,
    };
  }, [currentProductCode, currentProductDescription, currentProductName]);

  const additionalServices = useMemo(
    () => getAdditionalFraudPreventionServices(currentProductCode, enabledProductCodes),
    [currentProductCode, enabledProductCodes],
  );

  useEffect(() => {
    if (open) {
      setSelectedProductCodes(additionalServices.map((service) => service.productCode));
    }
  }, [additionalServices, open]);

  const toggleService = (productCode: string) => {
    if (isSubmitting) {
      return;
    }

    setSelectedProductCodes((current) =>
      current.includes(productCode) ? current.filter((code) => code !== productCode) : [...current, productCode],
    );
  };

  const handleModalClose = () => {
    if (!isSubmitting) {
      void onClose();
    }
  };

  const handleCancel = () => {
    if (!isSubmitting) {
      void onCancel();
    }
  };

  const handleContinue = () => {
    if (!isSubmitting) {
      void onContinue(selectedProductCodes);
    }
  };

  return (
    <Modal open={open} onClose={handleModalClose}>
      <ModalHeader
        title="Enable Additional Services"
        onClose={handleModalClose}
        closeButtonDataTestId={`${TEST_ID_PREFIX}-button-close`}
      />
      <ModalBody>
        <Box className={classes.section}>
          <Typography variant="h3" className={classes.sectionTitle}>
            Enabling Now
          </Typography>
          <ServiceRowContent
            service={enablingService}
            classes={classes}
            testId={`${TEST_ID_PREFIX}-enabling-now`}
            action={
              <CheckCircleOutline
                className={classes.enabledIcon}
                data-testid={`${TEST_ID_PREFIX}-enabling-now-check`}
              />
            }
          />
        </Box>

        {additionalServices.length > 0 ? (
          <Box className={classes.section}>
            <Typography variant="h3" className={classes.sectionTitle}>
              Additional Services
            </Typography>
            <Typography variant="body2" className={classes.sectionDescription}>
              Enable additional Fraud Prevention services using the same underwriter configuration.
            </Typography>
            <Box className={classes.serviceRows}>
              {additionalServices.map((service, index) => {
                const checked = selectedProductCodes.includes(service.productCode);

                return (
                  <ServiceRowContent
                    key={service.productCode}
                    service={service}
                    classes={classes}
                    selected={checked}
                    testId={`${TEST_ID_PREFIX}-selectablecard-${index}`}
                    action={
                      <Checkbox
                        className={classes.checkbox}
                        checked={checked}
                        disabled={isSubmitting}
                        onChange={() => toggleService(service.productCode)}
                        icon={<CheckBoxOutlineBlankIcon fontSize="small" color="secondary" />}
                        checkedIcon={<CheckBoxIcon fontSize="small" color="primary" />}
                        inputProps={{
                          'aria-label': `Enable ${service.displayName}`,
                        }}
                        data-testid={`${TEST_ID_PREFIX}-checkbox-${index}`}
                      />
                    }
                  />
                );
              })}
            </Box>
          </Box>
        ) : null}
      </ModalBody>
      <Box className={classes.modalFooter}>
        <AgentNetButton
          variant="outlined"
          color="primary"
          size="small"
          disabled={isSubmitting}
          onClick={handleCancel}
          data-testid={`${TEST_ID_PREFIX}-button-cancel`}
        >
          Cancel
        </AgentNetButton>
        <AgentNetButton
          variant="contained"
          color="primary"
          size="small"
          disabled={isSubmitting}
          loading={isSubmitting}
          onClick={handleContinue}
          data-testid={`${TEST_ID_PREFIX}-button-continue`}
        >
          Continue
        </AgentNetButton>
      </Box>
    </Modal>
  );
};
